import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import './GuestMenu.css';

const ALLERGEN_TAG_META: Record<string, { icon: string; color: string }> = {
    'Vegan': { icon: '🌱', color: '#22c55e' },
    'Jain': { icon: '🙏', color: '#f59e0b' },
    'Contains Nuts': { icon: '🥜', color: '#ef4444' },
    'Gluten Free': { icon: '🌾', color: '#06b6d4' },
    'Spicy': { icon: '🌶️', color: '#dc2626' },
    'Dairy Free': { icon: '🥛', color: '#8b5cf6' },
    'Sugar Free': { icon: '🚫', color: '#ec4899' },
    'Chef Special': { icon: '⭐', color: '#eab308' },
};

interface MenuItem {
    name: string;
    description: string;
    tags: string[];
    isVeg: boolean;
    isAvailable: boolean;
}

interface MenuCategory {
    _id: string;
    name: string;
    icon: string;
    dayLabel: string;
    isBeverage: boolean;
    items: MenuItem[];
}

const GuestMenu: React.FC = () => {
    const { accessToken, loading: authLoading } = useAuth();
    const history = useHistory();

    const [categories, setCategories] = useState<MenuCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [dayFilter, setDayFilter] = useState('');

    const fetchMenu = useCallback(async () => {
        try {
            const dayQ = dayFilter ? `?day=${encodeURIComponent(dayFilter)}` : '';
            const res = await fetch(`${API_BASE_URL}/api/menu${dayQ}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const data = await res.json();
            if (data.categories) {
                setCategories(data.categories);
                // Auto-expand first category
                if (data.categories.length > 0) {
                    setExpandedIds(new Set([data.categories[0]._id]));
                }
            }
        } catch (err) {
            console.error('Fetch menu error:', err);
        } finally {
            setLoading(false);
        }
    }, [accessToken, dayFilter]);

    useEffect(() => {
        if (!authLoading) {
            if (accessToken) fetchMenu();
            else history.push('/login');
        }
    }, [accessToken, authLoading, fetchMenu, history]);

    const toggleAccordion = (id: string) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Collect unique day labels
    const allDays = Array.from(new Set(categories.map(c => c.dayLabel).filter(Boolean)));

    const foodCategories = categories.filter(c => !c.isBeverage);
    const beverageCategories = categories.filter(c => c.isBeverage);

    if (loading) {
        return (
            <div className="gm-page">
                <div className="gm-loading">
                    <div className="gm-spinner"></div>
                    <p>Loading menu...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="gm-page">
            {/* Header */}
            <header className="gm-header">
                <div className="gm-header-icon">🍽️</div>
                <div>
                    <h1>Wedding Menu</h1>
                    <p>Explore today's curated feast</p>
                </div>
            </header>

            {/* Day filter pills */}
            {allDays.length > 0 && (
                <div className="gm-day-filter">
                    <button className={`gm-day-pill ${!dayFilter ? 'active' : ''}`} onClick={() => setDayFilter('')}>
                        📋 All Days
                    </button>
                    {allDays.map(day => (
                        <button
                            key={day}
                            className={`gm-day-pill ${dayFilter === day ? 'active' : ''}`}
                            onClick={() => setDayFilter(day)}
                        >
                            {day}
                        </button>
                    ))}
                </div>
            )}

            {/* Food Categories */}
            <main className="gm-content">
                {foodCategories.length === 0 && beverageCategories.length === 0 && (
                    <div className="gm-empty">
                        <span>🍴</span>
                        <h3>Menu not available yet</h3>
                        <p>The hosts are still preparing the menu. Check back soon!</p>
                    </div>
                )}

                {foodCategories.map(cat => (
                    <div key={cat._id} className={`gm-accordion ${expandedIds.has(cat._id) ? 'open' : ''}`}>
                        <button className="gm-accordion-header" onClick={() => toggleAccordion(cat._id)}>
                            <span className="gm-acc-icon">{cat.icon}</span>
                            <span className="gm-acc-title">{cat.name}</span>
                            <span className="gm-acc-count">{cat.items.length} items</span>
                            <span className="gm-acc-arrow">{expandedIds.has(cat._id) ? '▲' : '▼'}</span>
                        </button>
                        {expandedIds.has(cat._id) && (
                            <div className="gm-accordion-body">
                                {cat.items.map((item, idx) => (
                                    <div key={idx} className="gm-dish">
                                        <div className="gm-dish-left">
                                            <span className={`gm-veg-icon ${item.isVeg ? 'veg' : 'nonveg'}`}></span>
                                            <div>
                                                <span className="gm-dish-name">{item.name}</span>
                                                {item.description && <span className="gm-dish-desc">{item.description}</span>}
                                                {item.tags.length > 0 && (
                                                    <div className="gm-dish-tags">
                                                        {item.tags.map(t => {
                                                            const meta = ALLERGEN_TAG_META[t];
                                                            return (
                                                                <span key={t} className="gm-tag" style={{ color: meta?.color, borderColor: meta?.color }}>
                                                                    {meta?.icon} {t}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {cat.items.length === 0 && <p className="gm-no-dishes">Coming soon...</p>}
                            </div>
                        )}
                    </div>
                ))}

                {/* Beverage Section */}
                {beverageCategories.length > 0 && (
                    <>
                        <div className="gm-bev-divider">
                            <span>🍹</span>
                            <h2>Beverages & Bar</h2>
                            <p>Signature drinks & refreshments</p>
                        </div>
                        {beverageCategories.map(cat => (
                            <div key={cat._id} className={`gm-accordion beverage ${expandedIds.has(cat._id) ? 'open' : ''}`}>
                                <button className="gm-accordion-header" onClick={() => toggleAccordion(cat._id)}>
                                    <span className="gm-acc-icon">{cat.icon}</span>
                                    <span className="gm-acc-title">{cat.name}</span>
                                    <span className="gm-acc-count">{cat.items.length} items</span>
                                    <span className="gm-acc-arrow">{expandedIds.has(cat._id) ? '▲' : '▼'}</span>
                                </button>
                                {expandedIds.has(cat._id) && (
                                    <div className="gm-accordion-body">
                                        {cat.items.map((item, idx) => (
                                            <div key={idx} className="gm-dish bev-item">
                                                <div className="gm-dish-left">
                                                    <span className="gm-bev-emoji">{cat.icon}</span>
                                                    <div>
                                                        <span className="gm-dish-name">{item.name}</span>
                                                        {item.description && <span className="gm-dish-desc">{item.description}</span>}
                                                        {item.tags.length > 0 && (
                                                            <div className="gm-dish-tags">
                                                                {item.tags.map(t => {
                                                                    const meta = ALLERGEN_TAG_META[t];
                                                                    return (
                                                                        <span key={t} className="gm-tag" style={{ color: meta?.color, borderColor: meta?.color }}>
                                                                            {meta?.icon} {t}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </>
                )}
            </main>
        </div>
    );
};

export default GuestMenu;
